import React from 'react';
import { HiOutlinePencilAlt, HiOutlineTrash } from 'react-icons/hi';
import { formatNaira, getCurrencyTotals, isCurrencyField } from '../../utils/currency';

function DataTable({
  fields,
  records,
  onEdit,
  onDelete,
  selectedIds = [],
  onToggleSelect,
  onToggleSelectAll,
}) {
  const currencyTotals = getCurrencyTotals(records, fields);
  const totalsByField = currencyTotals.reduce((totals, item) => {
    totals[item.field] = item.total;
    return totals;
  }, {});
  const selectable = Boolean(onToggleSelect);
  const allVisibleSelected = records.length > 0 && records.every(record => selectedIds.includes(record.id));

  if (records.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-gray-200 bg-white px-6 py-12 text-center dark:border-neutral-800 dark:bg-neutral-900">
        <h3 className="text-base font-bold text-gray-900 dark:text-neutral-100">No records found</h3>
        <p className="mt-1 text-sm text-gray-500 dark:text-neutral-400">Records saved from Add New will appear here.</p>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm dark:border-neutral-800 dark:bg-neutral-900">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-100 text-sm">
          <thead className="bg-gray-50 dark:bg-neutral-950">
            <tr>
              {selectable && (
                <ColumnHeader>
                  <input
                    type="checkbox"
                    checked={allVisibleSelected}
                    onChange={() => onToggleSelectAll?.()}
                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    aria-label={allVisibleSelected ? 'Clear selected records' : 'Select all visible records'}
                  />
                </ColumnHeader>
              )}
              <ColumnHeader>Record ID</ColumnHeader>
              {fields.map(field => (
                <ColumnHeader key={field}>{field}</ColumnHeader>
              ))}
              <ColumnHeader>Created</ColumnHeader>
              <ColumnHeader>Actions</ColumnHeader>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white dark:divide-neutral-800 dark:bg-neutral-900">
            {records.map(record => (
              <tr key={record.id} className="hover:bg-gray-50 dark:hover:bg-neutral-800">
                {selectable && (
                  <td className="whitespace-nowrap px-4 py-3">
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(record.id)}
                      onChange={() => onToggleSelect(record.id)}
                      className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      aria-label={`Select ${record.record_code || record.record_id}`}
                    />
                  </td>
                )}
                <TableCell strong>{record.record_code || record.record_id}</TableCell>
                {fields.map(field => (
                  <TableCell key={field}>{formatValue(record.data?.[field], field)}</TableCell>
                ))}
                <TableCell>{formatDate(record.created_at)}</TableCell>
                <td className="whitespace-nowrap px-4 py-3">
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => onEdit?.(record)}
                      className="text-blue-600 transition hover:text-blue-800 dark:text-neutral-200 dark:hover:text-white"
                      aria-label={`Edit ${record.record_code || record.record_id}`}
                      title="Edit"
                    >
                      <HiOutlinePencilAlt className="text-lg" />
                    </button>
                    <button
                      type="button"
                      onClick={() => onDelete?.(record)}
                      className="text-red-500 transition hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                      aria-label={`Delete ${record.record_code || record.record_id}`}
                      title="Delete"
                    >
                      <HiOutlineTrash className="text-lg" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {currencyTotals.length > 0 && (
              <tr className="bg-blue-50 dark:bg-neutral-950">
                {selectable && <td className="whitespace-nowrap px-4 py-3" />}
                <TableCell strong>Total</TableCell>
                {fields.map(field => (
                  <TableCell key={field} strong={Boolean(totalsByField[field])}>
                    {totalsByField[field] ? formatNaira(totalsByField[field]) : ''}
                  </TableCell>
                ))}
                <TableCell />
                <td className="whitespace-nowrap px-4 py-3" />
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ColumnHeader({ children }) {
  return (
    <th className="whitespace-nowrap px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-gray-400">
      {children}
    </th>
  );
}

function TableCell({ children, strong = false }) {
  return (
    <td className={`whitespace-nowrap px-4 py-3 text-gray-600 dark:text-neutral-300 ${strong ? 'font-bold text-gray-900 dark:text-neutral-100' : ''}`}>
      {children}
    </td>
  );
}

function formatValue(value, field) {
  if (value === undefined || value === null || value === '') {
    return '-';
  }

  if (isCurrencyField(field)) {
    return formatNaira(value);
  }

  return String(value);
}

function formatDate(value) {
  if (!value) {
    return '-';
  }

  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value));
}

export default DataTable;
